import { PickCountryComponent } from './../pick-country/pick-country.component';
import { CguComponent } from './../cgu/cgu.component';
import { StoreService } from './../../../services/store.service';
import { CommonMessageService } from './../../../services/common-message.service';
import { FormGroup, FormBuilder, Validators, AbstractControl } from '@angular/forms';
import { Component, OnInit } from '@angular/core';
import { ModalController } from '@ionic/angular';
import { CommonService } from 'src/app/services/common.service';
import { UserService, LoginCredentials, LoginType } from 'src/app/services/user.service';

@Component({
  selector: 'app-login',
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss'],
})
export class LoginComponent implements OnInit {
  loginForm: FormGroup;
  registerForm: FormGroup;
  forgotPwdForm: FormGroup;
  hasAccount = true;
  forgotPwd = false;
  pays: any[];
  condition = [{
    label: ' j\'accepte les conditions générales',
    checked: false,
    value: 'Accepte'
  }];
  passwordControl: AbstractControl;

  showPassword = false;
  isSubmitting = false;

  // Anti brute-force côté client (la vraie protection est côté backend)
  private static readonly MAX_ATTEMPTS = 5;
  private static readonly LOCKOUT_MS = 30 * 1000;
  private loginAttempts = 0;
  private lockoutUntil = 0;

  // Détection du type de compte selon le domaine de l'email
  private static readonly INTERN_DOMAIN = '@orangemali.com';
  private static readonly EMAIL_PATTERN = /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/;

  constructor(private fb: FormBuilder,
    private modalController: ModalController,
    private commonMessager: CommonMessageService,
    private store: StoreService,
    private userService: UserService,
    private common: CommonService) {

  }

  ngOnInit() {
    this.buildLoginForm();
    this.buildRegisterForm();
    this.builForgetPwdForm();
    this.getPays();
  }

  private builForgetPwdForm() {
    this.forgotPwdForm = this.fb.group({
      'email': ['', [Validators.required, Validators.pattern(LoginComponent.EMAIL_PATTERN)]],
    });
  }

  private buildLoginForm() {
    this.loginForm = this.fb.group({
      'email': ['', [
        Validators.required,
        Validators.maxLength(150),
        Validators.pattern(LoginComponent.EMAIL_PATTERN),
      ]],
      'password': ['', [Validators.required, Validators.maxLength(128)]],
    });
  }

  get emailControl(): AbstractControl | null {
    return this.loginForm ? this.loginForm.get('email') : null;
  }

  get loginPasswordControl(): AbstractControl | null {
    return this.loginForm ? this.loginForm.get('password') : null;
  }

  togglePasswordVisibility() {
    this.showPassword = !this.showPassword;
  }

  get isLockedOut(): boolean {
    return this.lockoutUntil > Date.now();
  }

  get lockoutRemainingSeconds(): number {
    if (!this.isLockedOut) {
      return 0;
    }
    return Math.ceil((this.lockoutUntil - Date.now()) / 1000);
  }

  /** Détermine le type de compte à partir du domaine de l'email. */
  private resolveLoginType(email: string): LoginType {
    return this.isInternEmail(email) ? 'intern' : 'externe';
  }

  /** Indique si l'email correspond à un compte interne (@orangemali.com). */
  private isInternEmail(email: string): boolean {
    return (email || '').toString().trim().toLowerCase().endsWith(LoginComponent.INTERN_DOMAIN);
  }

  /** L'email saisi dans le formulaire d'enregistrement est un compte interne. */
  get isRegisterEmailIntern(): boolean {
    return this.registerForm ? this.isInternEmail(this.registerForm.get('loginEmail').value) : false;
  }

  /** L'email saisi dans le formulaire mot de passe oublié est un compte interne. */
  get isForgotEmailIntern(): boolean {
    return this.forgotPwdForm ? this.isInternEmail(this.forgotPwdForm.get('email').value) : false;
  }

  /** Polyfill de FormGroup.markAllAsTouched (Angular 8+) pour Angular 7. */
  private markFormGroupTouched(group: FormGroup) {
    Object.keys(group.controls).forEach(key => {
      const control = group.controls[key];
      control.markAsTouched();
      if (control instanceof FormGroup) {
        this.markFormGroupTouched(control);
      }
    });
  }

  /** Extrait un message lisible depuis l'erreur catchée. */
  private extractErrorMessage(error: any): string {
    if (!error) {
      return 'Une erreur est survenue. Veuillez réessayer.';
    }
    if (typeof error === 'string') {
      return error;
    }
    if (typeof error === 'object') {
      if (error.error && typeof error.error === 'object' && error.error.message) {
        return String(error.error.message);
      }
      if (error.error && typeof error.error === 'string') {
        return error.error;
      }
      if (error.message) {
        return String(error.message);
      }
    }
    return 'Identifiants invalides.';
  }

  close(data?) {
    this.modalController.dismiss(data);
  }

  private buildRegisterForm() {
    this.registerForm = this.fb.group({
      'nom': ['', [Validators.required]],
      'prenom': ['', [Validators.required]],
      'loginEmail': ['', [Validators.required]],
      'telephone': ['', [Validators.required, Validators.minLength(8)]],
      'motDePass': ['', [
        Validators.required,
        Validators.pattern(/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/)
      ]],
      'passwordConfirm': ['', [Validators.required, Validators.minLength(6)]],
      'sexe': ['', [Validators.required]],
      'dateNaissance': ['', [Validators.required]],
      'adresse': ['', [Validators.required]],
      'pays': ['', [Validators.required]]
    },
      {
        validator: (group) => {
          return group.get('motDePass').value === group.get('passwordConfirm').value ? null : { notSame: true };
        }
      });
    this.passwordControl = this.registerForm.get('motDePass');
  }

  showRegisterForm() {
    this.hasAccount = false;
    this.forgotPwd = false;
  }
  showLoginForm() {
    this.hasAccount = true;
    this.forgotPwd = false;
  }
  showForgotPwdForm() {
    this.forgotPwd = true;
    this.hasAccount = false;
  }

  async login() {
    if (this.isSubmitting) {
      return;
    }
    if (this.isLockedOut) {
      this.commonMessager.errorToast(
        `Trop de tentatives. Réessayez dans ${this.lockoutRemainingSeconds}s.`
      );
      return;
    }
    if (this.loginForm.invalid) {
      this.markFormGroupTouched(this.loginForm);
      return;
    }

    this.isSubmitting = true;
    const loading = await this.commonMessager.loading('Patientez un instant');
    try {
      const raw = this.loginForm.value;
      const email = (raw.email || '').toString().trim().toLowerCase();
      const password: string = raw.password;
      const type: LoginType = this.resolveLoginType(email);

      const credential: LoginCredentials = { type, email, password };

      const data = (await this.userService.login(credential).toPromise()) as any;
      this.loginAttempts = 0;
      this.lockoutUntil = 0;

      await this.store.setValue('accessToken', data.token);
      await this.store.setValue('loginType', type);
      this.userService.currentUser = data.postulant;
      if (data.postulant && data.postulant.email) {
        await this.store.setValue('email', data.postulant.email);
      }
      await this.store.setValue('postulant', data.postulant);
      window.location.href = '';
    } catch (error) {
      this.loginAttempts += 1;
      if (this.loginPasswordControl) {
        this.loginPasswordControl.reset('');
      }
      if (this.loginAttempts >= LoginComponent.MAX_ATTEMPTS) {
        this.lockoutUntil = Date.now() + LoginComponent.LOCKOUT_MS;
        this.loginAttempts = 0;
        this.commonMessager.errorToast(
          `Trop de tentatives échouées. Connexion bloquée ${LoginComponent.LOCKOUT_MS / 1000}s.`
        );
      } else {
        this.commonMessager.errorToast(this.extractErrorMessage(error));
      }
    } finally {
      this.isSubmitting = false;
      loading.dismiss();
    }
  }

  async valider() {
    if (this.isInternEmail(this.forgotPwdForm.get('email').value)) {
      this.commonMessager.errorToast(
        'Impossible de modifier le mot de passe. Veuillez contacter l\'administrateur.'
      );
      return;
    }
    const loading = await this.commonMessager.loading('Patientez un instant');
    try {
      const dataToSend = this.forgotPwdForm.value;
      const data = (await this.userService.valider(dataToSend).toPromise()) as any;
      this.commonMessager.successToast('un email a éte envoyé à l\'adresse ' + dataToSend.email);
    } catch (error) {
      this.commonMessager.errorToast('Erreur:  ' + error);
    } finally {
      loading.dismiss();
    }
  }

  async register() {
    if (this.isInternEmail(this.registerForm.get('loginEmail').value)) {
      this.commonMessager.errorToast(
        'Le compte existe déjà'
      );
      return;
    }
    const loading = await this.commonMessager.loading('Patientez un instant');
    try {
      const dataToSend = this.registerForm.value;
      const data = (await this.userService.register(dataToSend).toPromise()) as any;
      await this.store.setValue('accessToken', data.token);
      this.userService.currentUser = data.postulant;
      await this.store.setValue('email', data.postulant.email);
      await this.store.setValue('postulant', data.postulant);
      window.location.href = '';
    } catch (error) {
      this.commonMessager.alert(error, 'Erreur', '');
    } finally {
      loading.dismiss();
    }
  }
  async getPays() {
    this.pays = (await this.common.pays().toPromise()) as any;
  }
  checkChange(ev, a) {
    // pas de log
  }

  async cgu() {
    const modal = await this.modalController.create({
      component: CguComponent,
    });
    await modal.present();
  }

  async pickCountry(ev) {
    ev.stopPropagation();
    const modal = await this.modalController.create({
      component: PickCountryComponent,
      mode: 'ios',
      cssClass: 'transparent-modal',
      backdropDismiss: true,
      showBackdrop: true,
      animated: true,
    });
    await modal.present();
    const { data } = await modal.onDidDismiss();
    if (!data) {
      return;
    }
    this.registerForm.get('pays').setValue(data.paysFr);
  }
}
